import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useA11y } from "@/a11y/A11yProvider";
import { LANGUAGES } from "./languages";

const KEY = "aic-lang";
const VALID = new Set(LANGUAGES.map((l) => l.code));
const Ctx = createContext({ lang: "en", setLang: () => {}, t: (s) => s, easy: false, ready: true });

// Only the chosen language code is persisted (non-sensitive display preference).
const loadLang = () => {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.has(v) ? v : "en";
  } catch {
    return "en";
  }
};

export const I18nProvider = ({ children }) => {
  const { prefs } = useA11y();
  const easy = !!prefs.easyRead;
  const [lang, setLangState] = useState(loadLang);
  const [dicts, setDicts] = useState({});
  const files = useMemo(() => [lang !== "en" && lang, easy && `${lang}.easy`].filter(Boolean), [lang, easy]);

  useEffect(() => {
    files.forEach((f) => {
      if (dicts[f]) return;
      import(`./locales/${f}.json`)
        .then((m) => setDicts((d) => (d[f] ? d : { ...d, [f]: m.default })))
        .catch((err) => console.warn(`Translations for "${f}" could not be loaded.`, err));
    });
  }, [files, dicts]);

  useEffect(() => {
    const meta = LANGUAGES.find((l) => l.code === lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = meta?.rtl ? "rtl" : "ltr";
    try {
      localStorage.setItem(KEY, lang);
    } catch (err) {
      console.warn("Language preference could not be saved.", err);
    }
  }, [lang]);

  const setLang = useCallback((code) => VALID.has(code) && setLangState(code), []);
  const t = useCallback(
    (s) => {
      if (easy) {
        const e = dicts[`${lang}.easy`]?.[s];
        if (e) return e;
      }
      return dicts[lang]?.[s] ?? s;
    },
    [dicts, lang, easy]
  );
  const ready = files.every((f) => !!dicts[f]);

  return <Ctx.Provider value={{ lang, setLang, t, easy, ready }}>{children}</Ctx.Provider>;
};

export const useI18n = () => useContext(Ctx);
export const useT = () => useContext(Ctx).t;
